<template>
  <div class="pa-3">
    <v-row>
      <v-col>
        <div class="d-flex align-center ga-2">
          <div class="text-h6">
            Cargos
          </div>
          <t-tooltip>
            <div>
              Os cargos concedem aos usuários, de forma indireta, um conjunto predefinido de permissões.
            </div>
          </t-tooltip>
        </div>
      </v-col>
    </v-row>
    <v-row class="mt-0">
      <v-col cols="12">
        <v-chip-group
          v-model="model.roles"
          multiple
          :direction="mobile ? 'vertical' : 'horizontal'"
        >
          <v-chip
            v-for="role in roles"
            :key="role.id"
            :text="role.title"
            :value="role"
            variant="outlined"
            filter
            color="orange lighten-2"
          />
        </v-chip-group>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <div class="d-flex align-center ga-2">
          <div class="text-h6">
            Permissões
          </div>
          <t-tooltip>
            <div>
              As permissões podem ser concedidas ao usuário de forma direta, ao selecioná-las na listagem abaixo, ou indireta, através dos cargos.
            </div>
            <br>
            <div>
              Não é recomendado, embora seja permitido, que o conjunto final de permissões do usuário destoe muito do que os seus cargos definem.
              Portanto, evite adicionar novas permissões ao usuário ou remover as que são concedidas pelos cargos atribuidos.
            </div>
          </t-tooltip>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="filter.title"
          label="Nome"
          variant="outlined"
          dense
          color="orange lighten-2"
          density="compact"
          hide-details
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="filter.status"
          label="Situação"
          :items="statusInputItems"
          variant="outlined"
          hide-details
          dense
          color="orange lighten-2"
          density="compact"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="filter.role"
          label="Cargo"
          :items="[
            { title: 'Todos', value: '' },
            ...roles.map(r => ({ title: r.title, value: r.id }))
          ]"
          variant="outlined"
          hide-details
          dense
          color="orange lighten-2"
          density="compact"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <div class="d-flex flex-column flex-sm-row ga-2 text-caption">
          <v-chip>Total: {{ abilities.length }}</v-chip>
          <v-chip>Selecionadas: {{ selectedAbilities.length }}</v-chip>
          <v-chip>
            <div class="d-flex align-center ga-2">
              <div>Diretas: {{ directAbilities.length }}</div>
              <t-tooltip :icon-size="18">
                Permissões concedidas diretamente ao usuário, de forma independente dos cargos selecionados.
              </t-tooltip>
            </div>
          </v-chip>
          <v-chip>Visíveis: {{ filteredAbilities.length }}</v-chip>
        </div>
      </v-col>
    </v-row>
    <v-row class="mt-0">
      <v-col
        cols="12"
        style="display: grid"
        :style="{
          gridTemplateColumns: xs ? '1fr' : 'repeat(4, 1fr)',
        }"
      >
        <div
          v-for="ability in filteredAbilities"
          :key="ability.name"
          class="d-flex align-center ga-1"
        >
          <v-checkbox
            v-model="selectedAbilities"
            :label="ability.title"
            :value="ability"
            :value-comparator="(a, b) => a.name === b.name"
            color="primary"
            hide-details
            multiple
            :class="{'opacity-50': !roleAbilities.some(a => ability.name === a.name)}"
          />
          <t-tooltip v-if="ability.name in warnings" icon="fa-regular fa-warning">
            {{ warnings[ability.name] }}
          </t-tooltip>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts" setup>
import AbilityService from '@/services/AbilityService'
import RoleService from '@/services/RoleService'
import { useLoader } from '@/stores/loader'
import Ability from '@/types/Ability'
import Role from '@/types/Role'
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import TTooltip from '@/components/TTooltip.vue'
import { AdminUpdateRequest } from '@/services/AdminService'
import { partition } from '@/helpers/array'

const loader = useLoader()
const { mobile, xs } = useDisplay()

const model = defineModel<AdminUpdateRequest>({ required: true })

// --- Roles ---
const roles = ref<Role[]>([])

const selectedRoles = computed(() => {
  return roles.value.filter(r => model.value.roles.map(r => r.id).includes(r.id))
})

watch(
  () => model.value.roles,
  () => {
    /**
     * Remove allowed abilities that are no longer "direct" due to the selection
     * of a role that already provides them indirectly.
     *
     * Forbidden abilities must be kept in the array as way of preventing the
     * user from unintentionally giving permissions that were set as forbidden
     * prior to the `roles` array update.
     */
    model.value.abilities = model.value.abilities.filter((a) => {
      const indirect = roleAbilities.value.some(b => a.name === b.name)
      return a.forbidden === indirect
    })
  },
)

// --- Abilities ---
const abilities = ref<Ability[]>([])

const selectedAbilities = computed({
  get() {
    const [forbidden, direct] = partition<Ability>(model.value.abilities ?? [], a => a.forbidden)

    const indirect = roleAbilities.value.filter(a => !forbidden.some(b => a.name === b.name))

    return [...direct, ...indirect]
  },
  set(selected: Ability[]) {
    const direct = selected.filter(a => !roleAbilities.value.some(b => a.name === b.name))
    const forbidden = roleAbilities.value.filter(a => !selected.some(b => b.name === a.name))

    model.value.abilities = [
      ...direct.map(a => ({ ...a, forbidden: false })),
      ...forbidden.map(a => ({ ...a, forbidden: true })),
    ]
  },
})

// Abilities that are provided by the selected roles
const roleAbilities = computed(() => {
  return selectedRoles.value.flatMap(r => r.abilities ?? [])
})

// Selected abilities not provided by any of the selected roles
const directAbilities = computed(() => {
  return selectedAbilities.value.filter(a => !roleAbilities.value.some(b => a.name === b.name))
})

// --- Form Handling ---
const filter = ref({
  title: '',
  status: '',
  role: '',
})

const statusInputItems = ref([
  { title: 'Todas', value: '' },
  { title: 'Concedidas', value: 'granted' },
  { title: 'Concedidas Diretamente', value: 'directly-granted' },
  { title: 'Concedidas Indiretamente (Cargos)', value: 'indirectly-granted' },
])

const filteredAbilities = computed(() => {
  return abilities.value.filter((ability) => {
    const granted = selectedAbilities.value.some(a => a.name === ability.name)
    const directlyGranted = directAbilities.value.some(a => a.name === ability.name)
    const abilityRoles = roles.value.filter(r => r.abilities?.some(a => a.name === ability.name))

    return ability.title.toLowerCase().includes(filter.value.title.toLowerCase())
      && (filter.value.status === ''
        || (filter.value.status === 'granted' && granted)
        || (filter.value.status === 'directly-granted' && granted && directlyGranted)
        || (filter.value.status === 'indirectly-granted' && granted && !directlyGranted))
      && (filter.value.role === ''
        || abilityRoles?.some(r => r.id === filter.value.role))
  })
})

const warnings = computed<Record<string, string>>(() => {
  return model.value.abilities.reduce((acc, curr) => {
    const message = curr.forbidden
      ? 'Esta permissão faz parte de pelo menos um dos cargos selecionados, porém não está sendo concedida ao usuário.'
      : 'Esta permissão está sendo concedida de forma direta ao usuário.'
    return { ...acc, [curr.name]: message }
  }, {})
})

// --- Requests ---
async function fetch() {
  try {
    loader.increment()

    const [_roles, _abilities] = await Promise.all([
      (new RoleService()).index(1, '', 100),
      (new AbilityService()).index(1, '', 1000),
    ])

    roles.value = _roles.data.data
    abilities.value = _abilities.data.data.filter(a => a.name !== '*')

    // Handle the "*" alias ability for the master role
    const master = roles.value.find(r => r.name === 'master')

    if (master) {
      master.abilities = [ ...abilities.value ]
    }

    // Map the admin role objects to the ones that come from the roles rquest
    model.value.roles = model.value.roles.map(a => roles.value.find(b => b.id === a.id) ?? a)
  } finally {
    loader.decrement()
  }
}

// --- Hooks ---
onBeforeMount(async () => {
  fetch()
})
</script>
